import { useMotionValueEvent, useScroll } from 'motion/react'
import { memo, useCallback, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { LegalDocument } from '#app/data/legal/types'
import { cn } from '#app/utils/misc'
import { siteContent } from '#app/utils/site/content'
import {
	LEGAL_NAV,
	LEGAL_TOP_ID,
	attachLegalLinks,
	measureActiveSection,
	tocEntries,
} from '#app/utils/site/legal'
import { HEADER_SELECTOR } from '../SiteHeader'
import { BigHeading } from '../primitives'
import { LegalToc } from './LegalToc'

const FOCUS_RING =
	'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink focus-visible:ring-offset-2'

/**
 * The legal shell: a centred hero (glow, condensed title, the Terms /
 * Privacy / Cookies switcher, the dates), then the table of contents beside
 * a 74ch prose column. The TOC follows the reading position through the
 * scroll-spy rule in app/utils/site/legal.ts, driven by motion's scroll
 * value; the link behaviour (smooth scroll with the header offset, hash in
 * the URL, client-side Terms-to-Privacy links) is wired from a ref callback.
 * Terms, Privacy and Cookies all render through this one component.
 */
export function LegalPage({ doc }: { doc: LegalDocument }) {
	const navigate = useNavigate()
	const copy = siteContent.legal
	const entries = tocEntries(doc)
	const [activeIndex, setActiveIndex] = useState(-1)
	const articleRef = useRef<HTMLElement | null>(null)

	const { scrollY } = useScroll()
	useMotionValueEvent(scrollY, 'change', (y) => {
		const article = articleRef.current
		if (article) setActiveIndex(measureActiveSection(article, y))
	})

	// Ref callbacks instead of effects: the root wires the link behaviour for
	// as long as it is mounted; the article runs one spy pass on mount, so a
	// page opened at a hash lights the right entry before any scroll.
	const rootRef = useCallback(
		(node: HTMLElement | null) => {
			if (!node) return
			return attachLegalLinks(node, {
				headerSelector: HEADER_SELECTOR,
				navigate,
			})
		},
		[navigate],
	)
	const articleRefCallback = useCallback((node: HTMLElement | null) => {
		articleRef.current = node
		if (!node) return
		// Next frame: after the router's scroll restoration (a layout effect),
		// so a page reached by a link measures its own scroll position and not
		// the previous page's.
		const frame = requestAnimationFrame(() => {
			setActiveIndex(measureActiveSection(node, window.scrollY))
		})
		return () => {
			cancelAnimationFrame(frame)
			articleRef.current = null
		}
	}, [])

	return (
		<div ref={rootRef} className="tt-legal">
			<header
				id={LEGAL_TOP_ID}
				className="tt-legal-hero relative overflow-hidden px-5 pb-[clamp(36px,5vh,56px)] pt-[clamp(56px,9vh,104px)] text-center max-[521px]:pb-[30px] max-[521px]:pt-11"
			>
				<div className="relative">
					<BigHeading
						as="h1"
						className="text-[clamp(52px,7vw,112px)] text-tt-ink max-[521px]:text-[clamp(44px,13.5vw,64px)]"
					>
						{doc.title}
					</BigHeading>
					<nav
						aria-label="Legal pages"
						className="mt-[30px] inline-flex max-w-full flex-wrap justify-center gap-1.5 rounded-[26px] border border-tt-line bg-white p-[5px] shadow-tt-soft max-[521px]:mt-[22px] max-[521px]:flex max-[521px]:gap-1"
					>
						{LEGAL_NAV.map((entry) => {
							const current = entry.slug === doc.slug
							return (
								<Link
									key={entry.slug}
									to={entry.href}
									aria-current={current ? 'page' : undefined}
									className={cn(
										'whitespace-nowrap rounded-full px-[18px] py-2 text-[14px] font-medium text-tt-body transition-colors duration-[250ms] hover:bg-tt-inset max-[521px]:flex-1 max-[521px]:px-2 max-[521px]:text-center max-[521px]:text-[13px] max-[361px]:px-1.5 max-[361px]:text-[12px]',
										current &&
											'bg-tt-sage font-semibold text-tt-ink hover:bg-tt-sage',
										FOCUS_RING,
									)}
								>
									{entry.label}
								</Link>
							)
						})}
					</nav>
					{doc.meta.length > 0 ? (
						<p className="tt-legal-meta mt-[18px] text-[14px] text-tt-muted max-[521px]:text-[13px]">
							{doc.meta.map((line) => (
								<span key={line}>{line}</span>
							))}
						</p>
					) : null}
				</div>
			</header>

			<div className="mx-auto grid max-w-[1180px] grid-cols-1 px-5 pb-20 min-[981px]:grid-cols-[260px_minmax(0,1fr)] min-[981px]:gap-[clamp(40px,6vw,96px)] min-[981px]:px-10 min-[981px]:pb-[clamp(80px,12vh,140px)]">
				<LegalToc variant="card" entries={entries} activeIndex={activeIndex} />
				<div className="min-w-0">
					<LegalToc
						variant="compact"
						entries={entries}
						activeIndex={activeIndex}
					/>
					<LegalArticle
						doc={doc}
						articleRef={articleRefCallback}
						backToTop={copy.backToTop}
					/>
				</div>
			</div>
		</div>
	)
}

/**
 * The prose column, memoised on the (static) document. The scroll spy
 * re-renders LegalPage on every section change; without the memo React
 * re-applies each `dangerouslySetInnerHTML` (a new object every render),
 * which replaces the links inside the prose and drops keyboard focus from
 * a focused link (Tab to support@ after jumping from the TOC, for example)
 * to <body>.
 */
const LegalArticle = memo(function LegalArticle({
	doc,
	articleRef,
	backToTop,
}: {
	doc: LegalDocument
	articleRef: (node: HTMLElement | null) => (() => void) | undefined
	backToTop: string
}) {
	return (
		<article
			ref={articleRef}
			data-tt-legal-prose=""
			className="tt-legal-prose max-w-[74ch]"
		>
			<div
				className="tt-legal-intro"
				dangerouslySetInnerHTML={{ __html: doc.intro }}
			/>
			{doc.sections.map((section) => (
				<section key={section.id} aria-labelledby={section.id}>
					<h2 id={section.id}>{section.heading}</h2>
					<div dangerouslySetInnerHTML={{ __html: section.html }} />
				</section>
			))}
			<a
				href={`#${LEGAL_TOP_ID}`}
				className={cn(
					'mt-7 inline-flex items-center gap-2 rounded-sm text-[14px] font-semibold text-tt-ink no-underline hover:opacity-70',
					FOCUS_RING,
				)}
			>
				{backToTop}
				<svg
					className="h-[14px] w-[14px]"
					viewBox="0 0 16 16"
					fill="none"
					stroke="currentColor"
					strokeWidth={1.8}
					strokeLinecap="round"
					strokeLinejoin="round"
					aria-hidden="true"
				>
					<path d="M8 13V3M3.5 7.5L8 3l4.5 4.5" />
				</svg>
			</a>
		</article>
	)
})
